import { IPositionCandidate, IPositionWithCandidates } from '@types';

export type CandidateColumnId =
  | 'candidate'
  | 'workstream'
  | 'position'
  | 'stack'
  | 'tt'
  | 'technicalRanking'
  | 'softSkills'
  | 'materials'
  | 'nextSteps';

export type CandidateSortKey =
  | keyof IPositionCandidate
  | 'workstream'
  | 'position'
  | 'technicalRanking'
  | 'softSkills';

export interface ICandidateColumn {
  id: CandidateColumnId;
  label: string;
  sortKey: CandidateSortKey | null;
  width: string;
  align: 'left' | 'center' | 'right';
  defaultOn: boolean;
  alwaysOn?: boolean;
  dropPriority: number;
  hasData?: (candidate: IPositionCandidate) => boolean;
}

export const CandidateColumns: ICandidateColumn[] = [
  {
    id: 'candidate',
    label: 'Candidate',
    sortKey: 'firstName',
    width: '28%',
    align: 'left',
    defaultOn: true,
    alwaysOn: true,
    dropPriority: 0,
  },
  {
    id: 'workstream',
    label: 'Workstream',
    sortKey: 'workstream',
    width: '14%',
    align: 'left',
    defaultOn: false,
    dropPriority: 7,
  },
  {
    id: 'position',
    label: 'Position',
    sortKey: 'position',
    width: '16%',
    align: 'left',
    defaultOn: false,
    dropPriority: 6,
  },
  {
    id: 'stack',
    label: 'Main stack',
    sortKey: null,
    width: '20%',
    align: 'left',
    defaultOn: false,
    dropPriority: 5,
  },
  {
    id: 'technicalRanking',
    label: 'Technical ranking',
    sortKey: 'technicalRanking',
    width: '15%',
    align: 'left',
    defaultOn: true,
    dropPriority: 2,
    hasData: (candidate) => Boolean(candidate.ranking?.technicalInterview),
  },
  {
    id: 'softSkills',
    label: 'Soft skills',
    sortKey: 'softSkills',
    width: '15%',
    align: 'left',
    defaultOn: true,
    dropPriority: 4,
    hasData: (candidate) => Boolean(candidate.ranking?.softSkills),
  },
  {
    id: 'tt',
    label: 'TT score, %',
    sortKey: 'testTask',
    width: '11%',
    align: 'left',
    defaultOn: true,
    dropPriority: 1,
    hasData: (candidate) =>
      typeof candidate.testTask?.score === 'number' ||
      Boolean(candidate.testTask?.isPending),
  },
  {
    id: 'materials',
    label: 'Intro & resume',
    sortKey: null,
    width: '12%',
    align: 'left',
    defaultOn: true,
    dropPriority: 3,
  },
  {
    id: 'nextSteps',
    label: 'Next steps',
    sortKey: 'status',
    width: '26%',
    align: 'left',
    defaultOn: true,
    alwaysOn: true,
    dropPriority: 2,
  },
];

export const defaultCandidateColumns = (
  positions: IPositionWithCandidates[],
): CandidateColumnId[] =>
  CandidateColumns.filter(
    ({ defaultOn, hasData }) =>
      defaultOn &&
      (!hasData ||
        positions.some((position) =>
          (position.candidates ?? []).some(hasData),
        )),
  ).map((column) => column.id);

export const isCandidateColumnId = (id: string): id is CandidateColumnId =>
  CandidateColumns.some((column) => column.id === id);

export type CandidateColumnBand = 'tablet' | 'md' | 'lg';

const MaxColumnsByBand: Record<CandidateColumnBand, number> = {
  tablet: 4,
  md: 5,
  lg: CandidateColumns.length,
};

export const resolveCandidateColumns = (
  selected: CandidateColumnId[],
  band: CandidateColumnBand,
): ICandidateColumn[] => {
  const chosen = CandidateColumns.filter((column) =>
    selected.includes(column.id),
  );
  const limit = MaxColumnsByBand[band];

  if (chosen.length <= limit) return chosen;

  const droppable = [...chosen]
    .filter((column) => !column.alwaysOn)
    .sort((a, b) => b.dropPriority - a.dropPriority)
    .slice(0, chosen.length - limit)
    .map((column) => column.id);

  return chosen.filter((column) => !droppable.includes(column.id));
};
